import { createClient } from '@supabase/supabase-js';
import { createCrossSubdomainAuthStorage } from '@rehub/supabase/auth-storage';
import type { FloorPlan, UserProfile } from '../types';

const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || process.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY || '';

export const isConfigured = !!(supabaseUrl && supabaseAnonKey && supabaseUrl.startsWith('http') && !supabaseUrl.includes('placeholder'));

if (!isConfigured) {
  console.warn('[FloorPlan] Supabase env missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
}

const validUrl = isConfigured ? supabaseUrl : 'https://placeholder.supabase.co';
const validKey = isConfigured ? supabaseAnonKey : 'placeholder';

export const supabase = createClient(validUrl, validKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: createCrossSubdomainAuthStorage(),
  },
  global: { headers: {} },
});

// Stripe billing portal (invoices, payment methods, subscriptions). Shared
// across every ReHub app via @rehub/commerce + the single Supabase project.
import { openBillingPortal as openBillingPortalShared } from '@rehub/commerce';
export const openBillingPortal = (returnUrl?: string) =>
  openBillingPortalShared(supabase, returnUrl);

/* ─── Profile / credits ─────────────────────────────────── */

export const getProfile = async (userId: string): Promise<UserProfile | null> => {
  if (!isConfigured) return null;
  const { data, error } = await supabase.from('profiles').select('id, email, credits').eq('id', userId).single();
  if (error || !data) return null;
  return data as UserProfile;
};

/** Consume a single credit. For multi-credit operations, call in a loop. */
export const consumeCredit = async (userId: string): Promise<'success' | 'insufficient_credits' | 'error'> => {
  try {
    const { error } = await supabase.rpc('consume_credit', { user_id: userId });
    if (error) {
      if (error.message?.includes('INSUFFICIENT_CREDITS')) return 'insufficient_credits';
      console.error('[FloorPlan] consume_credit error:', error);
      return 'error';
    }
    return 'success';
  } catch (err) {
    console.error('[FloorPlan] consume_credit exception:', err);
    return 'error';
  }
};

/** Consume multiple credits (calls RPC once per credit). */
export const consumeCredits = async (userId: string, amount: number): Promise<'success' | 'insufficient_credits' | 'error'> => {
  for (let i = 0; i < amount; i++) {
    const result = await consumeCredit(userId);
    if (result !== 'success') {
      for (let j = 0; j < i; j++) await refundCredit(userId);
      return result;
    }
  }
  return 'success';
};

export const refundCredit = async (userId: string): Promise<void> => {
  try {
    await supabase.rpc('refund_credit', { user_id: userId });
  } catch (err) {
    console.error('[FloorPlan] refund_credit error:', err);
  }
};

/* ─── Floor plans CRUD ───────────────────────────────────── */

export const getFloorPlans = async (userId: string): Promise<FloorPlan[]> => {
  if (!isConfigured) return [];
  const { data, error } = await supabase
    .from('floor_plans')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });
  if (error) {
    console.error('[FloorPlan] getFloorPlans error:', error.message);
    return [];
  }
  return (data ?? []) as FloorPlan[];
};

export const getFloorPlanById = async (id: string, userId: string): Promise<FloorPlan | null> => {
  if (!isConfigured) return null;
  const { data, error } = await supabase
    .from('floor_plans')
    .select('*')
    .eq('id', id)
    .eq('user_id', userId)
    .single();
  if (error || !data) return null;
  return data as FloorPlan;
};

export const createFloorPlan = async (userId: string, name = 'Untitled Floor Plan'): Promise<FloorPlan | null> => {
  if (!isConfigured) return null;
  const { data, error } = await supabase
    .from('floor_plans')
    .insert({ user_id: userId, name })
    .select()
    .single();
  if (error) {
    console.error('[FloorPlan] createFloorPlan error:', error.message);
    return null;
  }
  return data as FloorPlan;
};

export const updateFloorPlan = async (
  id: string,
  userId: string,
  updates: Partial<Pick<FloorPlan, 'name' | 'canvas_data' | 'thumbnail_url' | 'scale_unit' | 'scale_value'>>
): Promise<FloorPlan | null> => {
  if (!isConfigured) return null;
  const { data, error } = await supabase
    .from('floor_plans')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('user_id', userId)
    .select()
    .single();
  if (error) {
    console.error('[FloorPlan] updateFloorPlan error:', error.message);
    return null;
  }
  return data as FloorPlan;
};

export const deleteFloorPlan = async (id: string, userId: string): Promise<boolean> => {
  if (!isConfigured) return false;
  const { error } = await supabase.from('floor_plans').delete().eq('id', id).eq('user_id', userId);
  if (error) {
    console.error('[FloorPlan] deleteFloorPlan error:', error.message);
    return false;
  }
  return true;
};

/* ─── Export log ─────────────────────────────────────────── */

export const logFloorPlanExport = async (
  floorPlanId: string,
  userId: string,
  format: 'png' | 'pdf' | 'svg',
  creditsUsed: number
): Promise<void> => {
  if (!isConfigured) return;
  try {
    await supabase.from('floor_plan_exports').insert({
      floor_plan_id: floorPlanId,
      user_id: userId,
      format,
      credits_used: creditsUsed,
    });
  } catch (err) {
    console.error('[FloorPlan] logFloorPlanExport error:', err);
  }
};
